<template>
    <Modal class="edit-program-modal" :is_show="is_show_edit_modal" @click-bg="close"
        :class="{ 'is-loading': store.state.is_loading }">
        <header class="edit-header">
            <h2>{{ is_edit ? 'Update' : 'Create'}} Your Application</h2>
            <i @click="close"><svg-icon name="close" /></i>
        </header>

        <Scrollbar class="edit-scroll-list" :show_bar="true">
            <Form :schema="schema" @submit="submit" :values="values" v-slot="{ errors, success }" class="user-form-wrap"
                :class="{ 'is-error': is_error }" ref="form">
                <div class="name-box">
                    <label class="label">
                        Application title <span class="req"> *</span>
                        <tippy :delay="[0, 20]" placement="right"  trigger="mouseenter click"
                            :content="`This is how applicants for this program will be categorized in the Anchor portal. Members outside of your organization may see this in the metadata for your application's web page and via search engines or social shares.`">
                            <svg-icon name="question-3" />
                        </tippy>
                    </label>
                    <Field type="text" k="application_title" :placeholder="`${brand_name} program`" :had_value="values.application_title"
                        :error_msg="errors.application_title" :is_success="success.application_title" @update="update"
                        :ref="(el) => { els.push(el) }" />
                </div>

                <div :class="['name-box', error_call_back.application_url.val && 'name-box-err']">
                    <label class="label">Application URL:<span class="req"> *</span></label>
                    <div class="input">
                        <label>{{ label_text }}</label>
                        <input type="text" :placeholder="`${brand_name} program`" v-model="values.application_url" :disabled="is_edit && application_status !== 'draft'">
                        <div class="err-client-msg-box" v-if="error_call_back.application_url.val">
                            <p class="err-msg" v-html="error_call_back.application_url.mes"></p>
                            <i><svg-icon name="alert" class="icon-left" /></i>
                        </div>
                    </div>
                </div>

                <div :class="['name-box', error_call_back.application_tag.val && 'name-box-err']">
                    <label class="label">Application tag<span class="req"> *</span>
                        <tippy :delay="[0, 20]" placement="right"  trigger="mouseenter click"
                            :content="`Applicants approved to activate ${brand_name} storefronts will be automatically labeled with this tag, viewable in the Creator Management dashboard.`">
                            <svg-icon name="question-3" />
                        </tippy>
                    </label>

                    <Field type="text" k="create_tag" :placeholder="`${brand_name} program`" :had_value="values.create_tag"
                        :error_msg="errors.create_tag" :is_success="success.create_tag" @update="update"
                        :ref="(el) => { els.push(el) }" />
                    <div class="err-client-msg-box err-client-msg-box-2" v-if="error_call_back.application_tag.val">
                        <p class="err-msg" v-html="error_call_back.application_tag.mes"></p>
                        <i><svg-icon name="alert" class="icon-left" /></i>
                    </div>
                </div>

                <div class="name-box account-type-box" v-if="!is_edit && !is_link_only">
                    <label class="label">
                        Creator Account Type<span class="req"> *</span>
                    </label>
                    <p class="desc">Select the account types available to your creators. If “Both” is selected, you will be prompted to choose an account experience for each individual creator upon approval. <span class="note">Note: This selection cannot be changed once published.</span></p>
                    <el-select
                        v-model="values.application_type"
                        :disabled="is_edit && application_status !== 'draft'"
                        :teleported="false"
                        popper-class="account-type-popper"
                        :popper-options="{ modifiers: [{ name: 'offset', options: { offset: [0, 0] } }] }"
                        @change="account_type_error = false"
                    >
                        <el-option label="Both" value="flexible" />
                        <el-option label="Storefront" value="storefront" />
                        <el-option label="Links" value="shortlink" />
                    </el-select>
                    <p class="err-msg account-type-err" v-if="account_type_error">Please select an account type.</p>
                </div>

                <div class="name-box" v-if="need_des">
                    <label class="label">Meta description
                        <tippy :delay="[0, 20]" placement="right"  trigger="mouseenter click"
                            :content="`The description entered will be viewable when people find your application via search engines and may even help it appear as a higher ranking. It may also appear in the link preview on social shares.`">
                            <svg-icon name="question-3" />
                        </tippy>
                    </label>

                    <Field type="textarea" k="meta_description" :placeholder="`Apply to be a part of ${brand_name}'s ${values.application_title} and unlock exclusive perks.`" :had_value="values.meta_description"
                        :error_msg="errors.meta_description" :is_success="success.meta_description" @update="update"
                        :ref="(el) => { els.push(el) }" />
                </div>

                <br>
                <div class="btn-wrap">
                    <el-button @click="close()">Cancel</el-button>
                    <el-button type="primary" native-type="submit">{{ need_des ? 'Save' : 'Create' }}</el-button>
                </div>
            </Form>
        </Scrollbar>
    </Modal>
</template>

<script setup>
import Field from '@/components/ui/Field.vue';
import Form from '@/components/ui/Form.vue';
import Modal from '@/components/ui/Modal.vue';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import { Tippy } from 'vue-tippy';
import * as yup from 'yup';
import { ref, computed, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { ElButton, ElSelect, ElOption } from 'element-plus';

const props = defineProps({
    need_des:                 { type: Boolean, default: false },
    is_edit:                  { type: Boolean, default: false },
    ambassador_application_id:{ type: Number,  default: 0 },
    application_status:       { type: String,  default: 'draft' },
});

const emit = defineEmits(['save']);

const store = useStore();
const { proxy } = getCurrentInstance();

const form = ref(null);
const is_show_edit_modal = ref(false);
const is_error = ref(false);
const error_msg = ref('');
const els = ref([]);
const account_type_error = ref(false);

const error_call_back = ref({
    application_url: { mes: 'This URL already exists.',          val: false },
    application_tag: { mes: 'This creator tag already exists.',  val: false },
});

const values = ref({
    application_title: '',
    application_url:   '',
    create_tag:        '',
    meta_description:  '',
    application_type:  'flexible',
});

const schema = yup.object().shape({
    application_title: yup.string().required("The application title can't be left blank"),
    application_url:   yup.string().required("The application URL can't be left blank"),
    create_tag: yup.string()
        .max(80, 'The character limit has been reached.')
        .matches(/^[a-zA-Z0-9.\-\s]+$/, `Creator tags must not contain special characters except ".", "-", and spaces.`)
        .matches(/^(?!\s*$).+/,         `Creator tags must contain at least one non-space character.`)
        .required("The tag can't be left blank"),
    meta_description: yup.string(),
});

const brand_name = computed(() => store.state.user_data.merchant_account.merchant_display_name);

const label_text = computed(() => {
    const url = store.state.user_data
        ? store.state.user_data.merchant_account.anchor_ambassador_subdomain
        : `${brand_name.value}.anchor.store`;
    return `${url}/p/`;
});

const permission_recruitment = computed(() =>
    store.state.user_data?.permission_list?.recruitment
);

const is_link_only = computed(() =>
    store.state.user_data?.merchant_account?.platform_type === 'linkonly'
);
//++++++++++++++++++++++++++++++++++++

const update = (obj) => {
    is_error.value = false;
    error_msg.value = '';
    Object.keys(values.value).forEach(key => {
        if (key === obj.key) values.value[key] = obj.val;
    });

    if (obj.key === 'application_title') {
        const cleanString = str => str.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
        values.value.application_url = cleanString(obj.val);
    }
    if (obj.key === 'application_url') {
        error_call_back.value[obj.key].val = false;
    }
    if (obj.key === 'create_tag') {
        error_call_back.value['application_tag'].val = false;
        if (obj.val) form.value.validate('create_tag');
    }
};

const submit = () => {
    if (!values.value.application_type) {
        account_type_error.value = true;
        return;
    }

    if (is_link_only.value) {
        values.value.application_type = 'shortlink';
    }

    is_error.value = false;
    store.state.is_loading = true;

    const type = props.need_des ? 'edit' : 'create';
    let api_url = `/ambassador/check-application-url-tag?application_url=${values.value.application_url}&application_tag=${values.value.create_tag}`;
    if (props.ambassador_application_id) {
        api_url += `&ambassador_application_id=${props.ambassador_application_id}`;
    }

    proxy.$ajax(api_url, { method: 'get' })
        .then(res => {
            const n1 = 'application_tag';
            const n2 = 'application_url';
            error_call_back.value[n1].val = !res.data.data[n1];
            error_call_back.value[n2].val = !res.data.data[n2];

            if (!error_call_back.value[n1].val && !error_call_back.value[n2].val) {
                emit('save', { type, val: values.value });
                close();
            }
            store.state.is_loading = false;
        })
        .catch(err => {
            const msg = err.response?.data?.message || err.response?.data?.data?.message;
            if (msg) {
                proxy.$notify(`<svg class="err-notify"><use xlink:href="#alert--sprite"></use></svg> ${msg}!`);
            }
            store.state.is_loading = false;
        });
};

const open = (val) => {
    els.value = [];
    is_error.value = false;
    error_msg.value = '';
    account_type_error.value = false;
    is_show_edit_modal.value = true;
    if (val) {
        values.value.application_title  = val[0].input_value;
        values.value.application_url    = val[1].input_value;
        values.value.create_tag         = val[2].input_value;
        values.value.meta_description   = val[3].input_value;
        const is_sf = val[4] ? val[4].input_value : false;
        const is_sl = val[5] ? val[5].input_value : false;
        values.value.application_type   = (is_sf && is_sl) ? 'flexible' : is_sf ? 'storefront' : 'shortlink';
    } else {
        values.value.application_title  = '';
        values.value.application_url    = '';
        values.value.create_tag         = '';
        values.value.meta_description   = '';
        values.value.application_type   = is_link_only.value ? 'shortlink' : 'flexible';
    }
};

const close = () => {
    is_show_edit_modal.value = false;
    is_error.value = false;
    error_msg.value = '';
};

defineExpose({ open });
</script>

<style lang="scss">
.edit-program-modal {
    .ui-modal--container {
        background-color: var(--bg-02);
        width: 620px;
        padding: 16px;
    }
    .edit-scroll-list{
        overflow-x: hidden;
    }

    .edit-header {
        display: flex;
        align-items: center;

        h2 {
            font: var(--font-demi-20);
            color: var(--black-90);
            flex: 1;
            line-height: 1;
        }

        i {
            height: 12px;
            width: 12px;
            position: relative;
            display: block;
            cursor: pointer;

            svg {
                height: 12px;
                width: 12px;
                fill: var(--black-50);
            }
        }
    }

    .user-form-wrap {
        margin-top: 16px;

        .name-box {
            position: relative;
        }

        .label {
            font: var(--font-fg-16);
            color: var(--black-90);
            display: flex;
            align-items: center;

            span{
                cursor: pointer;
            }

            .svg-icon{
                width: 12px;
                height: 12px;
                fill:var(--neutral);
            }

            .req {
                color: var(--alert);
            }
        }

        input {
            width: 100%;
            padding: 6px 8px;
            font-size: 14px;
            color: var(--black-90);
            outline: none;
            height: 36px;
            border: 1px solid var(--black-20);
            border-radius: 6px;

            &:focus {
                border: 1px solid var(--dark-blue);
            }
        }


        .input {
            position: relative;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            flex: 1;

            label {
                height: 36px;
                background: #CBD4E1;
                padding: 0 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                color: var(--black-90);
                border-radius: 6px 0px 0px 6px;
            }

            input {
                border-radius: 0 6px 6px 0;
                font-family: var(--font);
                flex:1;
                margin: 12px 0;

                &::placeholder {
                    color: var(--black-50);
                    font-weight: 500;
                }

                &:disabled{
                    cursor: not-allowed;
                }
            }
            .ui-input{
                flex:1;
            }
        }

        .desc {
            font-size: 14px;
            color: var(--black-75);
        }

        .ui-input {
            margin: 12px 0;
            position: relative;
        }

        .err-msg-box{
            position: absolute;
            right: 0;
        }

        .btn-wrap {
            display: flex;
            justify-content: flex-end;

            .ui-button {
                width: 110px;
                padding: unset;

                &:first-child{
                    margin-right: 16px;
                }
            }
        }

        &.is-error {

            .ui-input .ui-input--wrap select,
            .ui-input .ui-input--wrap input,
            .ui-input .ui-input--select select,
            .ui-input .ui-input--select input,
            .ui-input .ui-input--icon-wrap {
                border-color: var(--alert) !important;
            }

            .error-msg {
                margin: 4px 0 0;
                font-size: 12px;
                color: var(--alert);

                &::before {
                    content: '*';
                }
            }
        }

        .coll-chec {
            .ui-checkbox {
                margin-top: 8px;
            }
        }

        .name-box-err{
            .the-input input{
                border: 1px solid var(--alert);
            }
        }

        .account-type-box {

            .el-select {
                width: 100%;
            }

            .desc {
                font-size: 14px;
                color: var(--black-75);
                margin: 6px 0 12px;

                .note {
                    font-weight: 600;
                }
            }

            .account-type-checkboxes {
                display: flex;
                gap: 24px;
            }

            .account-type-check {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 14px;
                color: var(--black-90);
                cursor: pointer;

                input[type="checkbox"] {
                    width: 16px;
                    height: 16px;
                    cursor: pointer;
                    accent-color: var(--dark-blue);

                    &:disabled {
                        cursor: not-allowed;
                    }
                }
            }

            .account-type-err {
                margin-top: 8px;
                font-size: 12px;
                color: var(--alert);
            }
        }

        .err-client-msg-box{
            display: flex;
            align-items: center;
            justify-content: flex-end;
            color:var(--alert);
            position: absolute;
            bottom: -10px;
            right: 0;
            font-size: 12px;

            &.err-client-msg-box-2{
                bottom: -20px;
            }

            i{
                width: 14px;
                height: 14px;
                display: flex;
                justify-content: center;
                align-items: center;
                margin-left: 5px;
            }

            svg{
                width: 10.5px;
                height: 10.5px;
                fill:var(--alert);
            }
        }
    }

    &.is-loading {
        .user-form-wrap {
            opacity: .5;
            pointer-events: none;
        }
    }


}

.account-type-popper {
    margin-left: -6px;
    width: 100%;
}
</style>
